// Billing data layer.
//
// Wraps the Supabase tables + RPCs created by
// supabase/billing_module1_migration.sql. All callsites in src/hooks
// and src/components route through this file rather than touching the
// supabase client directly — same pattern as src/lib/posts.ts and
// src/lib/follows.ts. RLS is the security boundary; this file is for
// type-safe ergonomics.

import { supabase } from "@/lib/supabase";
import type {
  Plan,
  ScoringEligibility,
  SubscriptionTier,
  UserSubscription,
  WaitlistPlan,
} from "@/lib/billing/types";

// ---- Plans (catalog) --------------------------------------------------

/** Read-only catalog of all plans, ordered for display. RLS lets any
 *  authenticated user read. Returns [] on error rather than throwing —
 *  the pricing page renders a skeleton/empty state. */
export async function fetchPlans(): Promise<Plan[]> {
  const { data, error } = await supabase
    .from("subscription_plans")
    .select("id, name, tier, billing_period, price_inr, features")
    .eq("is_active", true)
    .order("sort_order", { ascending: true });
  if (error) {
    // eslint-disable-next-line no-console
    console.warn("[billing] fetchPlans failed", error);
    return [];
  }
  return (data ?? []) as Plan[];
}

// ---- User subscription ------------------------------------------------

/** Current user's active subscription row. null if not found (the
 *  signup trigger creates it; if it's missing for any reason callers
 *  should treat the user as free). */
export async function fetchMySubscription(): Promise<UserSubscription | null> {
  const { data, error } = await supabase
    .from("user_subscriptions")
    .select("id, user_id, plan_id, status, started_at, current_period_end")
    .eq("status", "active")
    .maybeSingle();
  if (error) {
    // eslint-disable-next-line no-console
    console.warn("[billing] fetchMySubscription failed", error);
    return null;
  }
  return (data as UserSubscription | null) ?? null;
}

/** Convenience: returns the user's tier as a single string. Uses the
 *  get_user_tier(uuid) SECURITY DEFINER RPC, so it works for OTHER
 *  users too (used by PlanBadge to label any profile in the app).
 *  Defaults to "free" on any error or missing row. */
export async function fetchUserTier(
  userId?: string,
): Promise<SubscriptionTier> {
  const { data, error } = await supabase.rpc("get_user_tier", {
    user_id_param: userId,
  });
  if (error) {
    // eslint-disable-next-line no-console
    console.warn("[billing] fetchUserTier failed", error);
    return "free";
  }
  const tier = data as string;
  return tier === "pro" || tier === "pro_plus" || tier === "unlimited"
    ? tier
    : "free";
}

// ---- AI scoring eligibility ------------------------------------------

/** Pre-flight check before kicking off analyze-video. Returns the full
 *  eligibility shape so the UI can render the "X/Y used" indicator
 *  AND the "limit reached" modal off the same query.
 *
 *  This is a UX check, NOT the enforcement point. The limit is enforced
 *  server-side by the analyze-video Edge Function (403 +
 *  FREE_LIMIT_REACHED) against the same counter this RPC reads — see
 *  supabase/report_quota_server_enforcement_migration.sql. Failing open
 *  here therefore costs nothing: the server still refuses. */
export async function checkScoringEligibility(): Promise<ScoringEligibility> {
  const { data, error } = await supabase.rpc("can_score_video");
  if (error) {
    // eslint-disable-next-line no-console
    console.warn("[billing] checkScoringEligibility failed", error);
    // Fail-OPEN: don't block an upload on a transient RPC problem when the
    // Edge Function will re-check anyway. Numbers mirror the free tier in
    // player_plan_limits(): 5 cards/month, 3-minute video, ads shown.
    return {
      allowed: true,
      used: 0,
      limit: 5,
      remaining: 5,
      tier: "free",
      code: null,
      topup_credits: 0,
      topup_eligible: false,
      daily_limit: null,
      daily_used: 0,
      seconds_until_daily_reset: null,
      video_max_seconds: 180,
      ad_free: false,
    };
  }
  return data as ScoringEligibility;
}

/* NOTE — recordAiScoring() was removed here.
 *
 * The usage counter used to be written by the browser (an INSERT into
 * ai_scoring_usage under an "insert own" RLS policy), which meant a
 * modified client could simply never record its usage. The counter is now
 * incremented by consume_report_quota(), called by the analyze-video Edge
 * Function with the service key after the strength_cards row commits. The
 * client insert policy has been dropped, so re-adding a client-side write
 * here would silently fail. */

// ---- Waitlist --------------------------------------------------------

/** Join the Pro / Pro+ waitlist with a target email. Email defaults to
 *  the user's account email if not provided. The UNIQUE constraint on
 *  (user_id, target_plan_id) means re-joining is a no-op. */
export async function joinWaitlist(args: {
  targetPlan: WaitlistPlan;
  email: string;
}): Promise<{ success: boolean; error?: string }> {
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData?.user?.id;
  if (!userId) return { success: false, error: "Not signed in" };
  const { error } = await supabase.from("subscription_waitlist").insert({
    user_id: userId,
    email: args.email,
    target_plan_id: args.targetPlan,
  });
  if (error) {
    // Duplicate-key (already on waitlist) → treat as success. RLS / unique
    // constraint failures both surface as PostgrestError; the unique one
    // has code "23505".
    if ("code" in error && (error as { code?: string }).code === "23505") {
      return { success: true };
    }
    return { success: false, error: error.message };
  }
  return { success: true };
}

/** Remove the current user from the named waitlist. */
export async function leaveWaitlist(targetPlan: WaitlistPlan): Promise<void> {
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData?.user?.id;
  if (!userId) return;
  await supabase
    .from("subscription_waitlist")
    .delete()
    .eq("user_id", userId)
    .eq("target_plan_id", targetPlan);
}

/** True iff the current user has an entry on the named waitlist. */
export async function checkOnWaitlist(
  targetPlan: WaitlistPlan,
): Promise<boolean> {
  const { data, error } = await supabase
    .from("subscription_waitlist")
    .select("id")
    .eq("target_plan_id", targetPlan)
    .maybeSingle();
  if (error) {
    // eslint-disable-next-line no-console
    console.warn("[billing] checkOnWaitlist failed", error);
    return false;
  }
  return !!data;
}
